import React, { useState, useEffect } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Badge, Card, CardBody } from 'reactstrap';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import ViewJobBid from './ViewJobBid';
import { Bid } from './BidShared';
import { getBaseMilestones, createPaymentMilestones } from '../Contract/Milestones';
import ConsolidatedContractorView from './ConsolidatedContractorView';
import { useUser } from '../../hooks';


interface HomeownerBidListProps {
  jobId: string;
  jobDetail: string;
}

const HomeownerBidList: React.FC<HomeownerBidListProps> = ({ jobId, jobDetail }) => {
  const [bids, setBids] = useState<Bid[]>([]);
  const [selectedBid, setSelectedBid] = useState<Bid | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [bidToDelete, setBidToDelete] = useState<Bid | null>(null);
  const [contractId, setContractId] = useState<string | null>(null);
  const [viewBidModalOpen, setViewBidModalOpen] = useState(false);
  const [selectedBidForView, setSelectedBidForView] = useState<string | null>(null);
  const [bidListKey, setBidListKey] = useState(0);
  const [activeTab, setActiveTab] = useState('1');

  const { currentUserId } = useUser();

  useEffect(() => {
    const fetchData = async () => {
      const firestore = firebase.firestore();

      // Fetch bids
      const bidsSnapshot = await firestore.collection('jobs').doc(jobId).collection('bids').get();
      const bidData = bidsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Bid));

      const sortedBids = bidData.sort((a, b) => {
        const aMillis = a.createdAt?.toMillis() ?? 0;
        const bMillis = b.createdAt?.toMillis() ?? 0;
        return bMillis - aMillis;
      });

      setBids(sortedBids);

      // Check if there's already an accepted bid
      const jobDoc = await firestore.collection('jobs').doc(jobId).get();
      const jobData = jobDoc.data();
      if (jobData && jobData.acceptedBidId) {
        const acceptedBid = sortedBids.find(bid => bid.id === jobData.acceptedBidId);
        if (acceptedBid) {
          setSelectedBid(acceptedBid);
          setContractId(jobData.contractId);
        }
      }
    };

    fetchData();
  }, [jobId, bidListKey]);

  const handleContractorsInvited = () => {
    setBidListKey(prev => prev + 1);
  };

  const toggleTab = (tab: string) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const handleAccept = (bid: Bid) => {
    setSelectedBid(bid);
    setIsConfirmModalOpen(true);
  };

  const handleDelete = (bid: Bid) => {
    setBidToDelete(bid);
    setIsDeleteModalOpen(true);
  };

  const handleViewBid = (bidId: string) => {
    setSelectedBidForView(bidId);
    setViewBidModalOpen(true);
  };

  const generateContractId = () => {
    return 'xxxxxxxxxxxxxxxx'.replace(/[x]/g, () => {
      return (Math.random() * 16 | 0).toString(16);
    });
  };

  const formatDate = (timestamp: firebase.firestore.Timestamp) => {
    const date = timestamp.toDate();
    return date.toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).replace(/(\d+)\/(\d+)\/(\d+), /, '$3-$2-$1 ');
  };

  const confirmAcceptBid = async () => {
    if (selectedBid) {
      try {
        const firestore = firebase.firestore();
        const newContractId = generateContractId();

        // Fetch job details
        const jobDoc = await firestore.collection('jobs').doc(jobId).get();
        const jobData = jobDoc.data();

        if (!jobData) {
          throw new Error('Job data not found');
        }

        // Generate milestones based on whether inspection is required
        const baseMilestones = getBaseMilestones(
          selectedBid.contractorId || '',
          currentUserId || '',
          selectedBid.requireInspection || false
        );

        // Generate payment milestones
        const paymentMilestones = createPaymentMilestones(
          selectedBid.paymentSchedule || [],
          parseFloat(selectedBid.bidAmount) || 0,
          selectedBid.contractorId || '',
          currentUserId || ''
        );

        // Combine all milestones
        const allMilestones = [...baseMilestones, ...paymentMilestones].sort((a, b) => a.sequence - b.sequence);

        // Create contract object with milestone data and default values for optional fields
        const contractData = {
          ownerId: currentUserId,
          contractId: newContractId,
          jobId,
          bidId: selectedBid.id || '',
          contractorId: selectedBid.contractorId || '',
          contractorName: selectedBid.contractorName || '',
          contractPrice: parseFloat(selectedBid.bidAmount) || 0,
          contractorPrice: parseFloat(selectedBid.bidAmountContractor) || 0,
          startDate: selectedBid.startDate || '',
          completionDate: selectedBid.endDate || '',
          approach: selectedBid.approach || '',
          costBreakdown: selectedBid.costBreakdown || [],
          bidType: selectedBid.bidType || 'fixedPrice',
          fixedPriceType: selectedBid.fixedPriceType || null,
          contractorLicense: selectedBid.contractorLicense || '',
          contractorAddress: selectedBid.contractorAddress || {
            address1: '',
            city: '',
            state: '',
            zipCode: ''
          },
          contractorPhone: selectedBid.contractorPhone || '',
          contractorEmail: selectedBid.contractorEmail || '',
          requireInspection: selectedBid.requireInspection || false,
          warrantyPeriod: selectedBid.warrantyPeriod || '365',
          terminationNoticeDays: selectedBid.terminationNoticeDays || '',
          delayDays: selectedBid.delayDays || '',
          attachments: selectedBid.attachments || [],
          ownerName: jobData.ownerName || '',
          ownerAddress: jobData.ownerAddress || {
            address1: '',
            city: '',
            state: '',
            zipCode: ''
          },
          ownerEmail: jobData.ownerEmail || '',
          ownerPhone: jobData.ownerPhone || '',
          propertyAddress: jobData.propertyAddress || {
            address1: '',
            city: '',
            state: '',
            zipCode: ''
          },
          contractDate: new Date().toISOString(),
          bidCreatedAt: selectedBid.createdAt || firebase.firestore.Timestamp.now(),
          jobDetail: jobData.jobDetail || '',
          paymentSchedule: selectedBid.paymentSchedule || [],
          milestones: allMilestones.map(milestone => ({
            id: milestone.id,
            status: milestone.status || 'pending',
            assigneeId: milestone.assigneeId,
            assigneeType: milestone.assigneeType,
            title: milestone.title,
            sequence: milestone.sequence,
            type: milestone.type,
            amount: milestone.amount || 0
          }))
        };

        // Create the contract document
        await firestore.collection('contracts').doc(newContractId).set(contractData);

        // Update job status
        await firestore.collection('jobs').doc(jobId).update({
          status: 'Contract Accepted',
          acceptedBidId: selectedBid.id,
          contractId: newContractId,
        });

        // Update bid status
        await firestore.collection('jobs').doc(jobId).collection('bids').doc(selectedBid.id).update({
          status: 'Accepted'
        });

        // Update the job-contractor match record
        const matchesSnapshot = await firestore
          .collection('jobContractorMatches')
          .where('jobId', '==', jobId)
          .where('contractorId', '==', selectedBid.contractorId)
          .limit(1)
          .get();

        if (!matchesSnapshot.empty) {
          await matchesSnapshot.docs[0].ref.update({
            status: 'bid',
            hasBid: true
          });
        }

        setContractId(newContractId);
        setIsConfirmModalOpen(false);

        // Update local state
        setBids(prevBids =>
          prevBids.map(bid =>
            bid.id === selectedBid.id ? { ...bid, status: 'Accepted' } : bid
          )
        );
      } catch (error) {
        console.error('Error creating contract:', error);
        // Handle error appropriately - maybe show an alert to the user
      }
    }
  };

  const confirmDeleteBid = async () => {
    if (bidToDelete) {
      const firestore = firebase.firestore();

      // Delete the bid
      await firestore.collection('jobs').doc(jobId).collection('bids').doc(bidToDelete.id).delete();

      // Update local state
      setBids(prevBids => prevBids.filter(bid => bid.id !== bidToDelete.id));

      setIsDeleteModalOpen(false);
      setBidToDelete(null);
    }
  };

  return (
    <div>
      <Card className="mb-4">
        <CardBody>

          <ConsolidatedContractorView
            jobId={jobId}
            bids={bids}
            onViewBid={handleViewBid}
            onAcceptBid={handleAccept}
            onDeleteBid={handleDelete}
            onContractorsInvited={handleContractorsInvited}
            contractId={contractId}
          />

        </CardBody>
      </Card>

      {/* View Bid Modal */}
      <Modal isOpen={viewBidModalOpen} toggle={() => setViewBidModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setViewBidModalOpen(false)}>View Bid Details</ModalHeader>
        <ModalBody>
          {selectedBidForView && <ViewJobBid jobId={jobId} bidId={selectedBidForView} />}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setViewBidModalOpen(false)}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Accept Bid Modal */}
      <Modal isOpen={isConfirmModalOpen} toggle={() => setIsConfirmModalOpen(false)}>
        <ModalHeader toggle={() => setIsConfirmModalOpen(false)}>Confirm Bid Acceptance</ModalHeader>
        <ModalBody>
          Are you sure you want to accept this bid from {selectedBid?.contractorName} for ${selectedBid?.bidAmount}?
          {selectedBid?.requireInspection && (
            <div className="mt-3 alert alert-warning">
              <strong>Note:</strong> This bid requires inspection before work begins.
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsConfirmModalOpen(false)}>Cancel</Button>
          <Button
            color={selectedBid?.requireInspection ? "warning" : "primary"}
            onClick={confirmAcceptBid}
          >
            Accept
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Bid Modal */}
      <Modal isOpen={isDeleteModalOpen} toggle={() => setIsDeleteModalOpen(false)}>
        <ModalHeader toggle={() => setIsDeleteModalOpen(false)}>Confirm Bid Deletion</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this bid from {bidToDelete?.contractorName} for ${bidToDelete?.bidAmount}?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setIsDeleteModalOpen(false)}>Cancel</Button>
          <Button color="danger" onClick={confirmDeleteBid}>Delete</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default HomeownerBidList;